import { SHOW_VOICE_ASSESSMENT_BETA_BADGE } from '@/src/config/beta-features'
import { PostPaymentRecordStep } from '@/src/features/onboarding/components/PostPaymentRecordStep'
import { VoiceAssessmentDynamicQuestionsStep } from '@/src/features/voice-assessment/components/VoiceAssessmentDynamicQuestionsStep'
import { VoiceAssessmentPersonalReflectionStep } from '@/src/features/voice-assessment/components/VoiceAssessmentPersonalReflectionStep'
import { VoiceAssessmentRecordingInstructionsStep } from '@/src/features/voice-assessment/components/VoiceAssessmentRecordingInstructionsStep'
import { useTranslation } from 'react-i18next'

import type { VoiceAssessmentFlowController } from './VoiceAssessmentFlow.shared'
import {
  countMeasurementsRequiringRecording,
  getCurrentScreen,
  getMeasurementAdvanceTarget,
  getProgressCurrent,
  getProgressTotal,
} from './VoiceAssessmentFlow.shared'

interface VoiceAssessmentFlowViewProps {
  controller: VoiceAssessmentFlowController
  isOnboardingEntry?: boolean
}

type OnboardingTranslate = ReturnType<
  typeof useTranslation<['onboarding']>
>['t']

const POST_PAYMENT_STEPS_BEFORE_ASSESSMENT_FLOW = 3

type MeasurementDefinitions =
  VoiceAssessmentFlowController['measurementDefinitions']

interface FlowProgress {
  currentStep: number
  totalSteps: number
}

function getRecordingMeasurementDisplayProgress({
  measurementDefinitions,
  measurementIndex,
}: {
  measurementDefinitions: MeasurementDefinitions
  measurementIndex: number
}): FlowProgress {
  return {
    currentStep: countMeasurementsRequiringRecording(
      measurementDefinitions.slice(0, measurementIndex + 1),
    ),
    totalSteps: countMeasurementsRequiringRecording(measurementDefinitions),
  }
}

function getDisplayedProgress({
  isOnboardingEntry,
  totalProgressSteps,
  progressCurrentStep,
}: {
  isOnboardingEntry: boolean
  totalProgressSteps: number
  progressCurrentStep: number
}): FlowProgress {
  const onboardingProgressOffset = isOnboardingEntry
    ? POST_PAYMENT_STEPS_BEFORE_ASSESSMENT_FLOW
    : 0
  const totalSteps = onboardingProgressOffset + totalProgressSteps
  const adjustedCurrentStep = onboardingProgressOffset + progressCurrentStep

  return {
    currentStep: isOnboardingEntry
      ? Math.min(totalSteps, adjustedCurrentStep)
      : adjustedCurrentStep,
    totalSteps,
  }
}

function renderVoiceAssessmentFlowView({
  controller,
  isOnboardingEntry,
  t,
}: {
  controller: VoiceAssessmentFlowController
  isOnboardingEntry: boolean
  t: OnboardingTranslate
}) {
  const {
    measurementDefinitions,
    measurementLabels,
    state,
    dispatch,
    requestLeaveStandaloneAssessment,
    isCompletingAssessment,
    handleCompleteAssessment,
    trackMeasurementRecordingCompleted,
    trackMeasurementQuestionsCompleted,
    persistMeasurement,
    persistingMeasurementIndex,
  } = controller

  const totalProgressSteps = getProgressTotal(measurementDefinitions)
  const currentScreen = getCurrentScreen(state, measurementDefinitions)
  const progressCurrentStep = getProgressCurrent(state, measurementDefinitions)
  const displayedProgress = getDisplayedProgress({
    isOnboardingEntry,
    totalProgressSteps,
    progressCurrentStep,
  })
  const advanceFromMeasurementStep = ({
    from,
    measurementIndex,
  }: {
    from: 'record' | 'questions'
    measurementIndex: number
  }) => {
    const advanceTarget = getMeasurementAdvanceTarget({
      from,
      measurementIndex,
      measurementDefinitions,
    })

    if (
      advanceTarget.kind !== 'questions' &&
      persistingMeasurementIndex != null
    ) {
      return
    }

    if (from === 'record') {
      trackMeasurementRecordingCompleted(measurementIndex)
    } else {
      trackMeasurementQuestionsCompleted(measurementIndex)
    }

    if (advanceTarget.kind === 'questions') {
      dispatch({ type: 'set_measurement_phase', phase: 'questions' })
      return
    }

    void persistMeasurement(measurementIndex)
      .then(() => {
        dispatch({
          type: 'set_measurement_index',
          index: advanceTarget.measurementIndex,
        })
      })
      .catch(() => {})
  }

  if (currentScreen.kind === 'recordingInstructions') {
    const measurementIndex = currentScreen.measurementIndex
    const measurementDefinition = measurementDefinitions[measurementIndex]
    return (
      <VoiceAssessmentRecordingInstructionsStep
        recordingInstructions={
          measurementDefinition?.recordingInstructions ?? ''
        }
        onNext={() =>
          dispatch({ type: 'set_measurement_phase', phase: 'record' })
        }
        onSkip={requestLeaveStandaloneAssessment}
        progressCurrentStep={displayedProgress.currentStep}
        progressTotalSteps={displayedProgress.totalSteps}
      />
    )
  }

  if (currentScreen.kind === 'record') {
    const measurementIndex = currentScreen.measurementIndex
    const measurementDefinition = measurementDefinitions[measurementIndex]
    const measurementLabel = measurementLabels[measurementIndex] ?? null
    const recordingUri = state.confirmedRecordingUris[measurementIndex] ?? null
    const isPersistingCurrentMeasurement =
      persistingMeasurementIndex === measurementIndex
    const handleRecordNext = () => {
      advanceFromMeasurementStep({ from: 'record', measurementIndex })
    }
    const recordingMeasurementDisplayProgress =
      getRecordingMeasurementDisplayProgress({
        measurementDefinitions,
        measurementIndex,
      })
    return (
      <PostPaymentRecordStep
        audioTrack={measurementDefinition?.audioTrack}
        onNext={handleRecordNext}
        onSkip={requestLeaveStandaloneAssessment}
        isSubmitting={isPersistingCurrentMeasurement}
        recordingInstruction={measurementDefinition?.recordingInstructions}
        progressCurrentStep={displayedProgress.currentStep}
        progressTotalSteps={displayedProgress.totalSteps}
        stepHeaderLabel={
          measurementLabel != null
            ? t(
                'onboarding:voice_assessment.header_named_measurement_recording',
                { name: measurementLabel },
              )
            : t('onboarding:voice_assessment.header_measurement_recording', {
                current: measurementIndex + 1,
              })
        }
        recordingCenterTitle={
          recordingMeasurementDisplayProgress.totalSteps > 1
            ? t('onboarding:voice_assessment.measurement_recording_title', {
                current: recordingMeasurementDisplayProgress.currentStep,
                total: recordingMeasurementDisplayProgress.totalSteps,
              })
            : null
        }
        showBetaBadge={SHOW_VOICE_ASSESSMENT_BETA_BADGE}
        restoredRecording={
          recordingUri != null
            ? {
                recordedAudioUri: recordingUri,
                confirmedRecordingUri: recordingUri,
              }
            : null
        }
        onConfirmedRecordingChange={(recording) =>
          dispatch({
            type: 'set_measurement_confirmed_recording',
            index: measurementIndex,
            uri: recording?.uri ?? null,
            blob: recording?.blob ?? null,
          })
        }
      />
    )
  }

  if (currentScreen.kind === 'questions') {
    const measurementIndex = currentScreen.measurementIndex
    const measurementLabel = measurementLabels[measurementIndex] ?? null
    const measurementDefinition = measurementDefinitions[measurementIndex]
    const answers = state.measurementQuestionAnswers[measurementIndex] ?? []
    const isPersistingCurrentMeasurement =
      persistingMeasurementIndex === measurementIndex
    const handleAdvance = () => {
      advanceFromMeasurementStep({ from: 'questions', measurementIndex })
    }
    return (
      <VoiceAssessmentDynamicQuestionsStep
        stepHeaderLabel={
          measurementLabel != null
            ? t(
                'onboarding:voice_assessment.header_named_measurement_questions',
                { name: measurementLabel },
              )
            : t('onboarding:voice_assessment.header_measurement_questions', {
                current: measurementIndex + 1,
              })
        }
        questions={measurementDefinition?.questions ?? []}
        answers={answers}
        onSingleChoiceChange={(questionId, choiceIndex) =>
          dispatch({
            type: 'set_measurement_single_choice_answer',
            index: measurementIndex,
            questionId,
            choiceIndex,
          })
        }
        onToggleMultipleChoice={(questionId, choiceIndex) =>
          dispatch({
            type: 'toggle_measurement_multiple_choice_answer',
            index: measurementIndex,
            questionId,
            choiceIndex,
          })
        }
        onNext={handleAdvance}
        onSkip={requestLeaveStandaloneAssessment}
        isSubmitting={isPersistingCurrentMeasurement}
        progressCurrentStep={displayedProgress.currentStep}
        progressTotalSteps={displayedProgress.totalSteps}
      />
    )
  }

  if (currentScreen.kind === 'reflection') {
    return (
      <VoiceAssessmentPersonalReflectionStep
        personalReflection={state.personalReflection}
        onPersonalReflectionChange={(value) =>
          dispatch({ type: 'set_personal_reflection', value })
        }
        onComplete={() => {
          void handleCompleteAssessment()
        }}
        onSkip={requestLeaveStandaloneAssessment}
        isSubmitting={isCompletingAssessment}
        progressCurrentStep={displayedProgress.currentStep}
        progressTotalSteps={displayedProgress.totalSteps}
        stepHeaderLabel={t('onboarding:voice_assessment.header_reflection')}
      />
    )
  }

  return null
}

export function VoiceAssessmentFlowView({
  controller,
  isOnboardingEntry = false,
}: VoiceAssessmentFlowViewProps) {
  const { t } = useTranslation(['onboarding'])

  return renderVoiceAssessmentFlowView({
    controller,
    isOnboardingEntry,
    t,
  })
}
